/**
 * Modern Variant Picker JavaScript
 * Version: 4.1 - LOCAL DATA (No API calls)
 *
 * Features:
 * - Gallery ↔ Variant bidirectional sync
 * - Metafield details modal with full a11y
 * - Touch swipe navigation
 * - WAI-ARIA compliant listbox with roving tabindex
 * - Custom inventory logic with location-based stock
 * - Variant reordering: in-stock first
 * - Parses variant title for additional details
 * - Event delegation (no inline onclick)
 * - XSS-safe DOM construction
 *
 * Changes in 4.1:
 * - Allow selecting unavailable variants (for back-in-stock notifications)
 *
 * Changes in 4.0:
 * - Removed all API calls - reads inventory from DOM data attributes
 * - Uses data-own-stock and data-external-stock rendered by Liquid
 * - Much faster - no network requests needed
 *
 * Changes in 3.1:
 * - Fixed: scroll to selected variant now happens AFTER inventory reorder
 *   (fixes issue where URL-selected variant wasn't visible after reorder)
 */

(function() {
  'use strict';

  // ==========================================================================
  // State & Cache
  // ==========================================================================
  const state = {
    isUpdatingFromGallery: false,
    metafieldsData: null,
    lastSyncedMediaId: null,
    initialized: false,
    modalLastFocusedElement: null,
    modalScrollPosition: 0
  };

  const cache = {
    modalOverlay: null,
    modalTable: null,
    variantPicker: null,
    wrapper: null,
    currentX: 0  // Track slider position globally for sync between scrollToWithinContainer and touch handlers
  };

  // ==========================================================================
  // Utility Functions
  // ==========================================================================
  
  /**
   * Find input element by value using direct comparison (bulletproof, no CSS.escape issues)
   * @param {Element} root - Parent element to search within
   * @param {string} selector - CSS selector for inputs
   * @param {string} value - Value to match
   * @returns {Element|null} Matching input or null
   */
  function findInputByValue(root, selector, value) {
    const inputs = root.querySelectorAll(selector);
    for (const input of inputs) {
      if (input.value === value) return input;
    }
    return null;
  }

  /**
   * Get authoritative variant option values keyed by variant id, read from the
   * embedded product JSON. This is the source of truth for option matching and
   * is immune to HTML-attribute encoding issues that can corrupt
   * data-variant-options (e.g. option values containing a double-quote).
   * @param {Element} variantPicker
   * @returns {Object<string, string[]>} map of variantId -> option values
   */
  function getVariantOptionsById(variantPicker) {
    if (variantPicker.__variantOptionsById) return variantPicker.__variantOptionsById;
    const map = {};
    try {
      const dataEl = variantPicker.querySelector('script[type="application/json"]');
      const data = JSON.parse(dataEl.textContent);
      const variants = (data.product && data.product.variants) || [];
      variants.forEach((v) => { map[v.id] = v.options; });
    } catch (e) {
      // Fall back to data attributes if JSON is unavailable
    }
    variantPicker.__variantOptionsById = map;
    return map;
  }

  /**
   * Wait for CustomInventoryAPI to be available
   */
  function waitForInventoryAPI(maxAttempts = 50, interval = 100) {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        if (window.CustomInventoryAPI) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, interval);
        } else {
          resolve(false);
        }
      };
      check();
    });
  }

  /**
   * Debounce function
   */
  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Format value - remove unnecessary decimals from integers (e.g., 3.0 → 3)
   */
  function formatValue(value) {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value.toString() : value;
    }
    if (typeof value === 'string' && /^\d+\.0+$/.test(value)) {
      return parseInt(value, 10).toString();
    }
    return value;
  }

  /**
   * Parse variant title into key-value pairs
   * Handles formats like: "Vikt g: 122.0 | Längd cm: 244 | Vavan pituus cm: 240-269 cm (8´)"
   * @param {string} title - Variant title to parse
   * @returns {Array<{key: string, value: string}>} Parsed key-value pairs
   */
  function parseVariantTitle(title) {
    if (!title || typeof title !== 'string') return [];
    
    const pairs = [];
    
    // Split by | separator
    const parts = title.split('|').map(p => p.trim()).filter(Boolean);
    
    for (const part of parts) {
      // Find the first colon to split key and value
      const colonIndex = part.indexOf(':');
      if (colonIndex > 0) {
        const key = part.substring(0, colonIndex).trim();
        const value = part.substring(colonIndex + 1).trim();
        if (key && value) {
          pairs.push({ key, value });
        }
      }
    }
    
    return pairs;
  }

  /**
   * Get translated label for an option key
   * Uses optionKeyMappings to find the metafield key, then uses labels for translation
   * @param {string} optionKey - Original key from variant title (e.g., "Vikt g")
   * @param {Object} data - Metafields data containing labels and optionKeyMappings
   * @returns {string} Translated label or original key
   */
  function getTranslatedLabel(optionKey, data) {
    if (!optionKey || !data) return optionKey;
    
    const { labels, optionKeyMappings } = data;
    
    // Try to find a mapping for this key (case-insensitive)
    const lowerKey = optionKey.toLowerCase();
    const metafieldKey = optionKeyMappings?.[lowerKey];
    
    if (metafieldKey && labels?.[metafieldKey]) {
      return labels[metafieldKey];
    }
    
    // Fallback: return original key with first letter capitalized
    return optionKey;
  }

  /**
   * Get metafields data from embedded JSON
   */
  function getMetafieldsData() {
    if (state.metafieldsData) return state.metafieldsData;
    
    const dataElement = document.getElementById('variant-metafields-data');
    if (!dataElement) return { labels: {}, variants: {}, product: {}, optionKeyMappings: {} };
    
    try {
      state.metafieldsData = JSON.parse(dataElement.textContent);
    } catch (e) {
      state.metafieldsData = { labels: {}, variants: {}, product: {}, optionKeyMappings: {} };
    }
    
    return state.metafieldsData;
  }

  /**
   * Scroll element into view within its container only (not the page)
   * Uses transform-based positioning
   */
  function scrollToWithinContainer(element, wrapper, smooth = true) {
    if (!element || !wrapper) return;

    const container = wrapper.closest('.variant-options-container');
    if (!container) return;

    const elementLeft = element.offsetLeft;
    const elementWidth = element.offsetWidth;
    const containerWidth = container.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const maxScroll = Math.max(0, wrapperWidth - containerWidth);

    // Center element in view
    let targetX = elementLeft - (containerWidth / 2) + (elementWidth / 2);
    targetX = Math.min(Math.max(0, targetX), maxScroll);

    wrapper.style.transition = smooth ? 'transform 0.3s ease-out' : 'none';
    wrapper.style.transform = `translateX(${-targetX}px)`;

    // Sync with global currentX so touch handlers stay in sync
    cache.currentX = targetX;

    // Update arrows after scroll
    const prevBtn = container.querySelector('.variant-scroll-btn--prev');
    const nextBtn = container.querySelector('.variant-scroll-btn--next');
    if (prevBtn) prevBtn.classList.toggle('visible', targetX > 10);
    if (nextBtn) nextBtn.classList.toggle('visible', targetX < maxScroll - 10);
  }

  // ==========================================================================
  // Inventory Functions
  // ==========================================================================

  /**
   * Reorder variant items: in-stock first, then out-of-stock
   */
  function reorderVariantItems(inventoryData, isExtendedAssortment) {
    if (!inventoryData || !window.CustomInventoryAPI) return;

    const wrapper = document.querySelector('.variant-options-wrapper');
    if (!wrapper) return;

    const variantItems = Array.from(wrapper.querySelectorAll('.variant-option-item'));
    if (variantItems.length <= 1) return;

    // Size swatch products: skip reordering, Liquid handles sort order
    const isSizeProduct = wrapper.classList.contains('variant-options-wrapper--size');
    if (isSizeProduct) return;

    const variantsWithAvailability = variantItems.map((item, originalIndex) => {
      const variantId = item.dataset.variantId;
      const inventory = inventoryData[variantId];
      const inventoryPolicy = item.dataset.inventoryPolicy || 'deny';
      const deliveryTime = item.dataset.deliveryTime || '';

      let isAvailable = false;
      if (inventory) {
        const status = CustomInventoryAPI.getStockStatus({
          ownStock: inventory.ownStock || 0,
          externalStock: inventory.externalStock || 0,
          inventoryPolicy,
          deliveryTime,
          isExtendedAssortment
        });
        isAvailable = status.isAvailable;
      }

      return { element: item, isAvailable, originalIndex };
    });

    variantsWithAvailability.sort((a, b) => {
      if (a.isAvailable === b.isAvailable) {
        return a.originalIndex - b.originalIndex;
      }
      return a.isAvailable ? -1 : 1;
    });

    variantsWithAvailability.forEach(({ element }) => {
      wrapper.appendChild(element);
    });

    // Update roving tabindex after reorder
    updateRovingTabindex();

    // Scroll to selected variant AFTER reorder (fixes position after URL variant selection)
    const selectedItem = wrapper.querySelector('.variant-option-item.selected');
    if (selectedItem) {
      requestAnimationFrame(() => {
        scrollToWithinContainer(selectedItem, wrapper, false);
      });
    }
  }

  /**
   * Update variant card unavailable states based on inventory
   */
  function updateVariantAvailability(inventoryData, isExtendedAssortment) {
    if (!inventoryData || !window.CustomInventoryAPI) return;

    const variantItems = document.querySelectorAll('.variant-option-item');
    
    variantItems.forEach(item => {
      const variantId = item.dataset.variantId;
      if (!inventoryData[variantId]) return;

      const { ownStock, externalStock } = inventoryData[variantId];
      const inventoryPolicy = item.dataset.inventoryPolicy || 'deny';
      const deliveryTime = item.dataset.deliveryTime || '';
      
      const status = CustomInventoryAPI.getStockStatus({
        ownStock,
        externalStock,
        inventoryPolicy,
        deliveryTime,
        isExtendedAssortment
      });

      item.classList.toggle('unavailable', !status.isAvailable);
    });
  }

  /**
   * Get inventory data from DOM data attributes (no API calls)
   * Reads data-own-stock and data-external-stock from variant items
   */
  function getInventoryDataFromDOM() {
    const inventoryData = {};
    const variantItems = document.querySelectorAll('.variant-option-item[data-variant-id]');

    variantItems.forEach(item => {
      const variantId = item.dataset.variantId;
      if (variantId) {
        inventoryData[variantId] = {
          ownStock: parseInt(item.dataset.ownStock, 10) || 0,
          externalStock: parseInt(item.dataset.externalStock, 10) || 0
        };
      }
    });

    return inventoryData;
  }

  /**
   * Initialize inventory for variant availability
   * Reads from DOM data attributes (no API calls)
   */
  function initInventoryAvailability() {
    // Wait for CustomInventoryAPI for getStockStatus function
    if (!window.CustomInventoryAPI) {
      // Retry after a short delay
      setTimeout(initInventoryAvailability, 100);
      return;
    }

    const variantPicker = document.querySelector('variant-picker.variant-picker-modern');
    if (!variantPicker) return;

    const isExtendedAssortment = variantPicker.dataset.isExtendedAssortment === 'true';

    // Get inventory data from DOM (no API call!)
    const inventoryData = getInventoryDataFromDOM();

    if (inventoryData && Object.keys(inventoryData).length > 0) {
      reorderVariantItems(inventoryData, isExtendedAssortment);
      updateVariantAvailability(inventoryData, isExtendedAssortment);
    }
  }

  // ==========================================================================
  // Modal Functions
  // ==========================================================================
  
  /**
   * Create table row safely using DOM methods (XSS-safe)
   */
  function createTableRow(label, value) {
    const tr = document.createElement('tr');
    const tdLabel = document.createElement('td');
    const tdValue = document.createElement('td');
    tdLabel.textContent = label;
    tdValue.textContent = formatValue(value);
    tr.appendChild(tdLabel);
    tr.appendChild(tdValue);
    return tr;
  }

  /**
   * Open variant details modal
   */
  function openVariantDetailsModal(variantId) {
    const data = getMetafieldsData();
    const variant = data.variants[variantId];
    const labels = data.labels;
    const productData = data.product || {};
    
    if (!variant) return;
    
    const table = cache.modalTable || document.getElementById('variant-details-table');
    if (!table) return;
    
    // Field order for metafields
    const fieldOrder = [
      'sku', 'tuotemerkki', 'malli', 'koko', 'kokoluokka', 'vari', 'materiaali',
      'pituus_cm', 'pituus_mm', 'pituus_m', 'tarvikkeen_pituus',
      'korkeus_cm', 'korkeus_m',
      'paino_g', 'paino_kg',
      'tilavuus_ml', 'tilavuus_l',
      'kelatyyppi', 'kelan_malli', 'katisyys', 'kapasiteetti_m_mm',
      'laakerien_maara', 'jarru', 'valitys',
      'vavan_pituus_cm', 'viehesuositus_g',
      'siimatyyppi', 'siiman_pituus_m', 'vetolujuus_kg', 'paksuus_mm',
      'koukkukoko', 'koukkujen_maara', 'koukkujen_maara_pussissa_kpl',
      'kelluvuus', 'kalastusluokka',
      'verkon_pituus_m', 'silmakoko_mm',
      'vaakatyyppi', 'nayton_koko',
      'pakkauskoko_kpl', 'osien_maara_kpl', 'varapuolat_kpl',
      'takuuaika'
    ];
    
    const productLevelFields = new Set(['takuuaika', 'tuotemerkki']);
    const addedKeys = new Set(['title']);
    const addedNormalizedKeys = new Set();
    
    // Clear table safely
    table.innerHTML = '';
    
    // 1. First add metafields in order (XSS-safe)
    for (const key of fieldOrder) {
      const value = productLevelFields.has(key) ? productData[key] : variant[key];
      
      if (value != null && value !== '') {
        table.appendChild(createTableRow(labels[key] || key, value));
        addedKeys.add(key);
        addedNormalizedKeys.add(key.toLowerCase());
      }
    }
    
    // 2. Add any remaining variant metafields not in order
    for (const key of Object.keys(variant)) {
      if (!addedKeys.has(key) && variant[key] != null) {
        table.appendChild(createTableRow(labels[key] || key, variant[key]));
        addedNormalizedKeys.add(key.toLowerCase());
      }
    }
    
    // 3. Parse variant title and add those values (if not already added via metafields)
    const titleParts = parseVariantTitle(variant.title);
    for (const { key, value } of titleParts) {
      const lowerKey = key.toLowerCase();
      const mappedKey = data.optionKeyMappings?.[lowerKey];
      
      if (mappedKey && addedNormalizedKeys.has(mappedKey.toLowerCase())) {
        continue;
      }
      
      const translatedLabel = getTranslatedLabel(key, data);
      table.appendChild(createTableRow(translatedLabel, value));
    }
    
    // Show modal
    const overlay = cache.modalOverlay || document.getElementById('variant-details-modal-overlay');
    if (overlay) {
      // Store last focused element for restoration
      state.modalLastFocusedElement = document.activeElement;

      // iOS-compatible scroll lock (improved)
      state.modalScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${state.modalScrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Prevent touch scroll on overlay (iOS)
      overlay.addEventListener('touchmove', preventModalBackgroundScroll, { passive: false });

      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');

      // Focus close button after transition
      setTimeout(function() {
        var closeBtn = overlay.querySelector('.variant-details-modal-close');
        if (closeBtn) closeBtn.focus();
      }, 100);

      // Add keyboard handler for modal
      document.addEventListener('keydown', handleModalKeydown);
    }
  }

  /**
   * Prevent background scroll on iOS when touching overlay
   */
  function preventModalBackgroundScroll(e) {
    var modalBody = e.target.closest('.variant-details-modal-body');
    if (!modalBody) {
      e.preventDefault();
    }
  }

  /**
   * Close variant details modal
   */
  function closeVariantDetailsModal(event) {
    if (event && event.target !== event.currentTarget) return;

    var overlay = cache.modalOverlay || document.getElementById('variant-details-modal-overlay');
    if (overlay && overlay.classList.contains('active')) {
      overlay.classList.remove('active');
      overlay.setAttribute('aria-hidden', 'true');

      // Remove touch scroll prevention
      overlay.removeEventListener('touchmove', preventModalBackgroundScroll);

      // Restore scroll position (iOS-compatible with RAF)
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      // Use requestAnimationFrame for smoother scroll restoration on iOS
      var scrollPos = state.modalScrollPosition;
      requestAnimationFrame(function() {
        window.scrollTo(0, scrollPos);
      });

      // Remove keyboard handler
      document.removeEventListener('keydown', handleModalKeydown);

      // Restore focus to trigger element
      if (state.modalLastFocusedElement && state.modalLastFocusedElement.focus) {
        state.modalLastFocusedElement.focus();
        state.modalLastFocusedElement = null;
      }
    }
  }

  /**
   * Handle modal keyboard events (Escape + focus trap)
   */
  function handleModalKeydown(e) {
    const overlay = cache.modalOverlay || document.getElementById('variant-details-modal-overlay');
    if (!overlay || !overlay.classList.contains('active')) return;
    
    // Escape closes modal
    if (e.key === 'Escape') {
      e.preventDefault();
      closeVariantDetailsModal();
      return;
    }
    
    // Focus trap on Tab
    if (e.key === 'Tab') {
      const modal = overlay.querySelector('.variant-details-modal');
      if (!modal) return;
      
      const focusables = modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusables.length) return;
      
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ==========================================================================
  // Variant Selection
  // ==========================================================================
  
  /**
   * Update roving tabindex - only selected item is tabbable
   */
  function updateRovingTabindex() {
    const wrapper = document.querySelector('.variant-options-wrapper');
    if (!wrapper) return;
    
    const items = wrapper.querySelectorAll('.variant-option-item');
    const selectedItem = wrapper.querySelector('.variant-option-item.selected');
    
    items.forEach(item => {
      const card = item.querySelector('.variant-option-card');
      if (!card) return;
      
      const isSelected = item === selectedItem;
      const isUnavailable = item.classList.contains('unavailable');
      
      // Roving tabindex: only selected is tabbable
      card.setAttribute('tabindex', isSelected ? '0' : '-1');
      
      // ARIA states
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      
      // aria-disabled for unavailable variants
      if (isUnavailable) {
        item.setAttribute('aria-disabled', 'true');
      } else {
        item.removeAttribute('aria-disabled');
      }
    });
  }

  /**
   * Select a variant from the modern picker
   * Note: Unavailable variants CAN be selected (for back-in-stock notifications)
   */
  function selectModernVariant(element, variantId, variantIndex, fromGallery = false) {
    if (fromGallery) {
      state.isUpdatingFromGallery = true;
    }
    
    const variantPicker = element.closest('variant-picker');
    if (!variantPicker) return;
    
    const wrapper = variantPicker.querySelector('.variant-options-wrapper');

    // Prefer authoritative option values from the product JSON (matched by variant
    // id). Falls back to the data attribute only if the variant isn't in the JSON.
    const optionsById = getVariantOptionsById(variantPicker);
    const lookupId = variantId != null ? variantId : element.dataset.variantId;
    const variantOptions = optionsById[lookupId]
      || (element.dataset.variantOptions ? element.dataset.variantOptions.split('|||') : []);
    
    // Update UI selection state
    variantPicker.querySelectorAll('.variant-option-item.selected').forEach(item => {
      item.classList.remove('selected');
      item.setAttribute('aria-selected', 'false');
      item.querySelector('.variant-option-card')?.setAttribute('tabindex', '-1');
    });
    
    element.classList.add('selected');
    element.setAttribute('aria-selected', 'true');
    element.querySelector('.variant-option-card')?.setAttribute('tabindex', '0');
    
    // Always scroll selected variant into view within container
    if (wrapper) {
      scrollToWithinContainer(element, wrapper, true);
    }
    
    // Trigger hidden radio inputs (set all first, then fire ONE change for Bundles compatibility)
    const optionSelectors = variantPicker.querySelectorAll('.option-selector');
    let lastChangedRadio = null;
    
    optionSelectors.forEach((selector, index) => {
      const optionValue = variantOptions[index];
      if (!optionValue) return;
      
      // Use direct value comparison instead of CSS.escape to avoid issues with special characters
      const radioInput = findInputByValue(selector, 'input', optionValue);
      if (radioInput && !radioInput.checked) {
        radioInput.checked = true;
        lastChangedRadio = radioInput; // remember last actually changed input
      }
    });
    
    // Fallback: if no radio was changed (all already checked), still dispatch change on first matched radio
    // This ensures variant-picker.js always processes the selection
    if (!lastChangedRadio) {
      const firstSelector = optionSelectors[0];
      const firstValue = variantOptions[0];
      if (firstSelector && firstValue) {
        const firstRadio = findInputByValue(firstSelector, 'input', firstValue);
        if (firstRadio) lastChangedRadio = firstRadio;
      }
    }
    
    // Fire a single change event so variant-picker.js computes the final variant once
    // This is critical for Shopify Bundles compatibility
    if (lastChangedRadio) {
      lastChangedRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Reset flag
    if (fromGallery) {
      requestAnimationFrame(() => {
        state.isUpdatingFromGallery = false;
      });
    }
  }

  // ==========================================================================
  // Gallery Sync
  // ==========================================================================
  
  /**
   * Initialize gallery synchronization
   */
  function initGallerySync() {
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;
    
    const mediaViewer = mediaGallery.querySelector('.media-viewer');
    if (!mediaViewer) return;
    
    const actualViewer = mediaViewer.querySelector('.media-viewer') || mediaViewer;
    
    /**
     * Get the currently visible media item
     */
    function getCurrentVisibleMedia() {
      const viewerRect = actualViewer.getBoundingClientRect();
      const viewerCenter = viewerRect.left + (viewerRect.width / 2);
      
      const mediaItems = actualViewer.querySelectorAll('.media-viewer__item');
      let closestItem = null;
      let closestDistance = Infinity;
      
      for (const item of mediaItems) {
        if (item.style.display === 'none' || item.clientWidth === 0) continue;
        
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + (itemRect.width / 2);
        const distance = Math.abs(viewerCenter - itemCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      }
      
      return closestItem;
    }

    /**
     * Sync variant based on visible media
     */
    function syncVariantFromVisibleMedia() {
      if (state.isUpdatingFromGallery) return;

      // Size swatch products: skip gallery-to-variant sync
      const sizeWrapper = document.querySelector('.variant-options-wrapper--size');
      if (sizeWrapper) return;
      
      const currentMedia = getCurrentVisibleMedia();
      if (!currentMedia) return;
      
      const mediaId = currentMedia.dataset.mediaId;
      if (!mediaId || mediaId === state.lastSyncedMediaId) return;
      
      // Only auto-switch the variant when the image maps to EXACTLY ONE variant.
      // Shared images would otherwise hijack the user's selection to whichever
      // card happens to be first in the DOM (a source of "wrong variant" bugs).
      const mediaMatches = document.querySelectorAll(
        `.variant-option-item[data-variant-image-id="${mediaId}"]`
      );
      const matchingVariant = mediaMatches.length === 1 ? mediaMatches[0] : null;

      if (matchingVariant && !matchingVariant.classList.contains('selected')) {
        state.lastSyncedMediaId = mediaId;
        selectModernVariant(
          matchingVariant,
          matchingVariant.dataset.variantId,
          parseInt(matchingVariant.dataset.variantIndex, 10),
          true
        );
      } else {
        state.lastSyncedMediaId = mediaId;
      }
    }

    const debouncedSync = debounce(syncVariantFromVisibleMedia, 150);
    
    actualViewer.addEventListener('scroll', debouncedSync, { passive: true });
    
    const mediaThumbs = mediaGallery.querySelector('.media-thumbs');
    mediaThumbs?.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-media-id]')) {
        setTimeout(syncVariantFromVisibleMedia, 300);
      }
    });
    
    const mediaCtrl = mediaGallery.querySelector('.media-ctrl');
    mediaCtrl?.addEventListener('click', (evt) => {
      if (evt.target.closest('.media-ctrl__btn')) {
        setTimeout(syncVariantFromVisibleMedia, 500);
      }
    });
    
    setTimeout(syncVariantFromVisibleMedia, 1000);
  }

  // ==========================================================================
  // Navigation & Scroll
  // ==========================================================================
  
  /**
   * Initialize navigation controls - Transform-based slider (no native scroll)
   */
  function initNavigation() {
    const containers = document.querySelectorAll('.variant-options-container');

    containers.forEach(container => {
      const wrapper = container.querySelector('.variant-options-wrapper');
      const prevBtn = container.querySelector('.variant-scroll-btn--prev');
      const nextBtn = container.querySelector('.variant-scroll-btn--next');

      if (!wrapper || !prevBtn || !nextBtn) return;

      cache.wrapper = wrapper;

      // Slider state - use cache.currentX for global sync with scrollToWithinContainer
      let touchStartX = 0;
      let touchStartY = 0;
      let touchCurrentX = 0;
      let isDragging = false;
      let startTime = 0;

      /**
       * Get maximum scroll value (how far left we can go)
       */
      function getMaxScroll() {
        const wrapperWidth = wrapper.scrollWidth;
        const containerWidth = container.clientWidth;
        return Math.max(0, wrapperWidth - containerWidth);
      }

      /**
       * Clamp value between min and max
       */
      function clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }

      /**
       * Set wrapper position with transform
       */
      function setPosition(x, animate = false) {
        const maxScroll = getMaxScroll();
        cache.currentX = clamp(x, 0, maxScroll);

        wrapper.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
        wrapper.style.transform = `translateX(${-cache.currentX}px)`;

        updateArrows();
      }

      /**
       * Update arrow visibility
       */
      function updateArrows() {
        const maxScroll = getMaxScroll();
        prevBtn.classList.toggle('visible', cache.currentX > 10);
        nextBtn.classList.toggle('visible', cache.currentX < maxScroll - 10);
      }

      /**
       * Scroll by one item
       */
      function scrollByItem(direction) {
        const item = wrapper.querySelector('.variant-option-item');
        const scrollAmount = (item?.offsetWidth || 100) + 12;
        const newX = direction === 'next' ? cache.currentX + scrollAmount : cache.currentX - scrollAmount;
        setPosition(newX, true);
      }

      // Touch events
      wrapper.addEventListener('touchstart', function(e) {
        isDragging = true;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchCurrentX = touchStartX;
        startTime = Date.now();
        wrapper.style.transition = 'none';
        // Enable GPU acceleration during scroll
        wrapper.classList.add('is-scrolling');
      }, { passive: true });

      wrapper.addEventListener('touchmove', (e) => {
        if (!isDragging) return;

        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        const diffX = touchStartX - x;
        const diffY = touchStartY - y;

        // Only handle horizontal swipes
        if (Math.abs(diffX) > Math.abs(diffY)) {
          e.preventDefault();
          const delta = touchCurrentX - x;
          touchCurrentX = x;

          // Calculate new position with hard limits (no overscroll)
          const maxScroll = getMaxScroll();
          const newX = clamp(cache.currentX + delta, 0, maxScroll);

          wrapper.style.transform = `translateX(${-newX}px)`;
          cache.currentX = newX;
        }
      }, { passive: false });

      wrapper.addEventListener('touchend', function(e) {
        if (!isDragging) return;
        isDragging = false;

        var diffX = touchStartX - e.changedTouches[0].clientX;
        var diffY = touchStartY - e.changedTouches[0].clientY;
        var elapsed = Date.now() - startTime;

        // Apply momentum if fast swipe
        if (Math.abs(diffX) > Math.abs(diffY) && elapsed < 300 && Math.abs(diffX) > 30) {
          var velocity = diffX / elapsed;
          var momentum = velocity * 150;
          setPosition(cache.currentX + momentum, true);
        } else {
          setPosition(cache.currentX, true);
        }

        updateArrows();

        // Remove GPU acceleration after scroll completes
        setTimeout(function() {
          wrapper.classList.remove('is-scrolling');
        }, 350);
      }, { passive: true });

      // Mouse events for desktop
      wrapper.addEventListener('mousedown', function(e) {
        isDragging = true;
        touchStartX = e.clientX;
        touchCurrentX = touchStartX;
        startTime = Date.now();
        wrapper.style.transition = 'none';
        wrapper.style.cursor = 'grabbing';
        // Enable GPU acceleration during scroll
        wrapper.classList.add('is-scrolling');
        e.preventDefault();
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;

        const delta = touchCurrentX - e.clientX;
        touchCurrentX = e.clientX;

        const maxScroll = getMaxScroll();
        const newX = clamp(cache.currentX + delta, 0, maxScroll);

        wrapper.style.transform = `translateX(${-newX}px)`;
        cache.currentX = newX;
      });

      document.addEventListener('mouseup', function() {
        if (!isDragging) return;
        isDragging = false;
        wrapper.style.cursor = '';
        setPosition(cache.currentX, true);
        updateArrows();
        // Remove GPU acceleration after scroll completes
        setTimeout(function() {
          wrapper.classList.remove('is-scrolling');
        }, 350);
      });

      // Button clicks
      prevBtn.addEventListener('click', () => scrollByItem('prev'));
      nextBtn.addEventListener('click', () => scrollByItem('next'));

      // Initial setup
      updateArrows();
      window.addEventListener('resize', debounce(() => {
        setPosition(cache.currentX, false);
      }, 100));

      // Wait for images to load before calculating dimensions
      const images = wrapper.querySelectorAll('img');
      let loadedCount = 0;
      const totalImages = images.length;

      if (totalImages === 0) return;

      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === totalImages) updateArrows();
          }, { once: true });
        }
      });
    });
  }

  // ==========================================================================
  // Keyboard Navigation (WAI-ARIA Listbox pattern with roving tabindex)
  // ==========================================================================
  
  function initKeyboardNav() {
    document.addEventListener('keydown', (e) => {
      // Skip if modal is open (modal has its own handler)
      const overlay = cache.modalOverlay || document.getElementById('variant-details-modal-overlay');
      if (overlay?.classList.contains('active')) return;
      
      const activeElement = document.activeElement;
      const wrapper = activeElement?.closest('.variant-options-wrapper');
      
      if (!wrapper) return;
      
      const items = [...wrapper.querySelectorAll('.variant-option-item')];
      const currentItem = activeElement.closest('.variant-option-item');
      const currentIndex = items.indexOf(currentItem);
      
      if (currentIndex === -1) return;
      
      // Arrow key navigation (roving tabindex)
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        let nextIndex = currentIndex + direction;
        
        // Wrap around
        if (nextIndex < 0) nextIndex = items.length - 1;
        if (nextIndex >= items.length) nextIndex = 0;
        
        const nextItem = items[nextIndex];
        const nextCard = nextItem?.querySelector('.variant-option-card');
        
        if (nextCard) {
          // Update tabindex (roving)
          activeElement.setAttribute('tabindex', '-1');
          nextCard.setAttribute('tabindex', '0');
          nextCard.focus();
          
          // Scroll into view within container
          scrollToWithinContainer(nextItem, wrapper, true);
        }
      }
      
      // Home/End keys for first/last
      if (e.key === 'Home') {
        e.preventDefault();
        const firstCard = items[0]?.querySelector('.variant-option-card');
        if (firstCard) {
          activeElement.setAttribute('tabindex', '-1');
          firstCard.setAttribute('tabindex', '0');
          firstCard.focus();
          scrollToWithinContainer(items[0], wrapper, true);
        }
      }
      
      if (e.key === 'End') {
        e.preventDefault();
        const lastCard = items[items.length - 1]?.querySelector('.variant-option-card');
        if (lastCard) {
          activeElement.setAttribute('tabindex', '-1');
          lastCard.setAttribute('tabindex', '0');
          lastCard.focus();
          scrollToWithinContainer(items[items.length - 1], wrapper, true);
        }
      }
      
      // Enter/Space selects the focused variant (including unavailable for back-in-stock)
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();

        if (currentItem) {
          selectModernVariant(
            currentItem,
            currentItem.dataset.variantId,
            parseInt(currentItem.dataset.variantIndex, 10)
          );
        }
      }
    });
  }

  // ==========================================================================
  // Event Delegation
  // ==========================================================================
  
  /**
   * Initialize event delegation for variant picker clicks
   * Replaces inline onclick handlers
   */
  function initEventDelegation() {
    const picker = cache.variantPicker;
    if (!picker) return;
    
    // Delegate all clicks within variant picker
    picker.addEventListener('click', (e) => {
      // Details button clicked
      const detailsBtn = e.target.closest('.variant-details-btn');
      if (detailsBtn) {
        e.stopPropagation();
        const item = detailsBtn.closest('.variant-option-item');
        if (item?.dataset.variantId) {
          openVariantDetailsModal(item.dataset.variantId);
        }
        return;
      }
      
      // Variant item/card clicked (select variant, including unavailable for back-in-stock)
      const item = e.target.closest('.variant-option-item');
      if (item && !item.classList.contains('selected')) {
        selectModernVariant(
          item,
          item.dataset.variantId,
          parseInt(item.dataset.variantIndex, 10)
        );
      }
    });
    
    // Bundle option change handler
    picker.addEventListener('change', (e) => {
      const bundleInput = e.target.closest('.js-bundle-option');
      if (bundleInput) {
        handleBundleOptionChange(bundleInput);
      }
    });
    
    // Modal overlay click to close
    const overlay = cache.modalOverlay;
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        // Only close if clicking the overlay itself, not the modal content
        if (e.target === overlay) {
          closeVariantDetailsModal();
        }
      });
      
      // Close button click
      const closeBtn = overlay.querySelector('.variant-details-modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => closeVariantDetailsModal());
      }
    }
  }

  // ==========================================================================
  // Bundle Components Support
  // ==========================================================================
  
  /**
   * Handle bundle option selection change
   * Syncs with hidden option selectors and triggers variant change
   */
  function handleBundleOptionChange(input) {
    const picker = cache.variantPicker;
    if (!picker) return;
    
    const optionPosition = parseInt(input.dataset.optionPosition, 10);
    const selectedValue = input.value;
    
    // Update visual state of bundle buttons
    const buttonGroup = input.closest('.bundle-option-buttons');
    if (buttonGroup) {
      buttonGroup.querySelectorAll('.bundle-option-btn').forEach(btn => {
        btn.classList.remove('bundle-option-btn--selected');
      });
      const selectedLabel = input.nextElementSibling;
      if (selectedLabel) {
        selectedLabel.classList.add('bundle-option-btn--selected');
      }
    }
    
    // Find and update the corresponding hidden option selector
    const hiddenSelectors = picker.querySelectorAll('.option-selector');
    hiddenSelectors.forEach(selector => {
      const inputs = selector.querySelectorAll('.js-option');
      inputs.forEach(hiddenInput => {
        if (hiddenInput.value === selectedValue) {
          hiddenInput.checked = true;
          // Dispatch change event to trigger variant-picker.js logic
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    
    // Dispatch custom event for any external listeners
    picker.dispatchEvent(new CustomEvent('bundle:option:change', {
      bubbles: true,
      detail: {
        position: optionPosition,
        value: selectedValue
      }
    }));
  }
  
  /**
   * Initialize bundle component options
   * Sets up initial state and syncs with variant picker
   */
  function initBundleComponents() {
    const picker = cache.variantPicker;
    if (!picker) return;
    
    const bundleSelector = picker.querySelector('[data-bundle-selector]');
    if (!bundleSelector) return;
    
    // Sync bundle options with current variant selection
    const selectedVariantItem = picker.querySelector('.variant-option-item.selected');
    if (selectedVariantItem) {
      const variantOptions = selectedVariantItem.dataset.variantOptions;
      if (variantOptions) {
        const options = variantOptions.split('|||');
        
        // Update bundle option buttons to match selected variant
        bundleSelector.querySelectorAll('[data-bundle-option]').forEach(optionGroup => {
          const position = parseInt(optionGroup.dataset.bundleOption, 10);
          const targetValue = options[position - 1];
          
          if (targetValue) {
            // Use direct value comparison instead of CSS.escape
            const matchingInput = findInputByValue(optionGroup, '.js-bundle-option', targetValue);
            if (matchingInput && !matchingInput.checked) {
              matchingInput.checked = true;
              // Update visual state
              optionGroup.querySelectorAll('.bundle-option-btn').forEach(btn => {
                btn.classList.remove('bundle-option-btn--selected');
              });
              const label = matchingInput.nextElementSibling;
              if (label) {
                label.classList.add('bundle-option-btn--selected');
              }
            }
          }
        });
      }
    }
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================
  
  function init() {
    if (state.initialized) return;
    state.initialized = true;
    
    // Cache DOM elements
    cache.modalOverlay = document.getElementById('variant-details-modal-overlay');
    cache.modalTable = document.getElementById('variant-details-table');
    cache.variantPicker = document.querySelector('variant-picker.variant-picker-modern');
    
    // Move modal to body
    if (cache.modalOverlay && cache.modalOverlay.parentElement !== document.body) {
      document.body.appendChild(cache.modalOverlay);
    }
    
    // Set initial aria-hidden on modal
    if (cache.modalOverlay) {
      cache.modalOverlay.setAttribute('aria-hidden', 'true');
    }
    
    // Initialize event delegation (replaces inline onclick)
    initEventDelegation();
    
    // Initialize components
    initNavigation();
    initGallerySync();
    initKeyboardNav();
    initInventoryAvailability();
    initBundleComponents();
    
    // Initialize roving tabindex
    updateRovingTabindex();
    
    // Scroll to selected variant WITHIN container only
    const wrapper = document.querySelector('.variant-options-wrapper');
    const firstSelected = document.querySelector('.variant-option-item.selected');
    
    if (firstSelected && wrapper) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToWithinContainer(firstSelected, wrapper, false);
        }, 100);
      });
    }
  }

  // ==========================================================================
  // Global API
  // ==========================================================================
  
  window.VariantPickerModern = {
    select: selectModernVariant,
    openDetails: openVariantDetailsModal,
    closeDetails: closeVariantDetailsModal
  };
  
  // Legacy global functions for inline onclick handlers
  window.selectModernVariant = selectModernVariant;
  window.openVariantDetailsModal = openVariantDetailsModal;
  window.closeVariantDetailsModal = closeVariantDetailsModal;

  // ==========================================================================
  // Start
  // ==========================================================================
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();